import * as React from 'react'
import { useEffect } from 'react'
import { Title } from './title'
import style from './user.css'
import { UserInfo } from 'modules/dashboard/user/user-info'
import { ResetUserPassword } from 'modules/dashboard/user/reset-user-password'
import { AddUserReturnValue } from 'interfaces/utils/add-user'
import { ModalLayerStore } from 'stores/modal-layer-store'
import { observer } from 'mobx-react-lite'
import { DashboardUsersStore } from 'modules/dashboard/user/user-store'
import { IUser } from 'interfaces/api/portal/user-api'
import { Loader } from 'components/loader'
import { DashboardLayout } from '../common/dashboard-layout'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { showAlert } from 'utils/show-alert'
import { TrialExpiredOrTerminated } from 'modules/account-setup/trial-expired-or-terminated/trial-expired-or-terminated'
import classNames from 'classnames'
import { UsersTab } from './users-tab'
import { UserSearchResult } from './search/user-search-result'
import { Export } from 'modules/export/export'
import { useTranslation } from 'react-i18next'
import { when } from 'mobx'
import { CloneUserModal } from './clone-user-modal'
import { ErrorScreen } from 'components/error-screen'
import { CloneUserProgressModal } from './clone-user-progress-modal/clone-user-progress-modal'
import { isTerminated } from 'helpers/company-helpers'
import { Button, SearchInputField, Box, notify, Toggler, Icons } from 'plume-ui'
import { AddEditUser } from './add-user/add-edit-user'
import { canUserClone } from 'helpers/user-helpers'
import { mixpanelActions } from 'utils/mixpanelActions'
import { CsvPercentageRing } from './csv-percentage-ring'
import { useStores } from 'utils/hooks/useStores'

export const showAddUserModal = async (
  actionModalStore: ModalLayerStore,
  usersStore: DashboardUsersStore,
) => {
  return new Promise<AddUserReturnValue>(res => {
    const onClose = async (closeButton: AddUserReturnValue) => {
      usersStore.clearFormState()
      actionModalStore.removeModal()
      return res(closeButton)
    }
    usersStore.initAddEditUserWebSocketEvents()
    actionModalStore.addModal(
      <AddEditUser
        key={actionModalStore.getKey()}
        onClose={() => onClose(AddUserReturnValue.CloseButton)}
        action="add"
      />,
    )
  })
}

export const showCloneUserModal = async (
  actionModalStore: ModalLayerStore,
  usersStore: DashboardUsersStore,
  user: IUser,
) => {
  return new Promise<AddUserReturnValue>(res => {
    const onClose = async (closeButton: AddUserReturnValue) => {
      usersStore.clearCloneUserForm()
      actionModalStore.removeModal()
      return res(closeButton)
    }
    usersStore.fillCloneUserForm(user)
    actionModalStore.addModal(
      <CloneUserModal
        key={actionModalStore.getKey()}
        onClose={() => {
          usersStore.fetchUsers()

          return onClose(AddUserReturnValue.CloseButton)
        }}
      />,
    )
  })
}

export const showCloneUserProgressModal = async (actionModalStore: ModalLayerStore) => {
  return new Promise<AddUserReturnValue>(res => {
    const onClose = async (closeButton: AddUserReturnValue) => {
      actionModalStore.removeModal()
      return res(closeButton)
    }
    actionModalStore.addModal(
      <CloneUserProgressModal
        key={actionModalStore.getKey()}
        onClose={() => {
          return onClose(AddUserReturnValue.CloseButton)
        }}
      />,
    )
  })
}

export const showEditUserModal = async (
  actionModalStore: ModalLayerStore,
  usersStore: DashboardUsersStore,
  user: IUser,
) => {
  return new Promise<AddUserReturnValue>(res => {
    const onClose = async (closeButton: AddUserReturnValue) => {
      usersStore.clearFormState()
      actionModalStore.removeModal()
      return res(closeButton)
    }
    usersStore.initAddEditUserWebSocketEvents()
    usersStore.fillEditForm(user)
    actionModalStore.addModal(
      <AddEditUser
        key={actionModalStore.getKey()}
        onClose={() => onClose(AddUserReturnValue.CloseButton)}
        action="edit"
      />,
    )
  })
}

export const showUserInfoModal = async (
  actionModalStore: ModalLayerStore,
  usersStore: DashboardUsersStore,
  user: IUser,
) => {
  return new Promise<AddUserReturnValue>(async res => {
    const onClose = async (closeButton: AddUserReturnValue) => {
      usersStore.clearUserInfoFields()
      actionModalStore.removeModal()
      return res(closeButton)
    }
    await usersStore.fillUserInfoFields(user)
    actionModalStore.addModal(
      <UserInfo
        key={actionModalStore.getKey()}
        onClose={() => onClose(AddUserReturnValue.CloseButton)}
      />,
    )
  })
}

export const showResetPasswordModal = async (
  actionModalStore: ModalLayerStore,
  usersStore: DashboardUsersStore,
  user: IUser,
) => {
  return new Promise<AddUserReturnValue>(res => {
    const onClose = async (closeButton: AddUserReturnValue) => {
      usersStore.clearResetPasswordForm()
      actionModalStore.removeModal()
      return res(closeButton)
    }
    usersStore.fillResetPasswordForm(user)
    actionModalStore.addModal(
      <ResetUserPassword
        key={actionModalStore.getKey()}
        onClose={() => onClose(AddUserReturnValue.CloseButton)}
      />,
    )
  })
}

interface IDashboardUsersParams {
  partnerId: string
}

interface IDashboardUserProps extends RouteComponentProps<IDashboardUsersParams> {}

const DashboardUsersComponent = observer((props: IDashboardUserProps) => {
  const { t } = useTranslation()
  const appStore = useStores()
  const { authStore, usersStore, actionModalStore } = appStore

  const {
    match: {
      params: { partnerId },
    },
  } = props

  useEffect(() => {
    authStore.update(partnerId)

    const disposer = when(
      () => !!authStore.currentUser && !!authStore.currentUser.company && !!appStore.usersStore,
      () => {
        const us = appStore.usersStore
        if (us && us.users.length === 0) {
          us.fetchUsers()
        }
      },
    )

    return disposer
  }, [partnerId, authStore, usersStore])

  // componentDidMount equivalent
  useEffect(() => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId
    mixpanelActions.track('Pageview - Users', {
      'Page name': 'Users',
      'Partner Id': currentPartnerId,
    })
  }, [authStore.currentUser?.company?.partnerId])

  // componentWillUnmount equivalent
  useEffect(() => {
    return () => {
      usersStore?.handleEndSearch()
    }
  }, [usersStore])

  const handleAddUser = () => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - Add a user', { 'Partner Id': currentPartnerId })
    showAddUserModal(actionModalStore, usersStore)
  }

  const handleEditUser = (user: IUser) => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - Edit user', { 'Partner Id': currentPartnerId })
    showEditUserModal(actionModalStore, usersStore, user)
  }

  const handleUserInfo = (user: IUser) => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - User Info', { 'Partner Id': currentPartnerId })
    showUserInfoModal(actionModalStore, usersStore, user)
  }

  const handleResetPassword = (user: IUser) => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - Reset Password', { 'Partner Id': currentPartnerId })
    showResetPasswordModal(actionModalStore, usersStore, user)
  }

  const handleCloneUser = (user: IUser) => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - Clone User', { 'Partner Id': currentPartnerId })
    showCloneUserModal(actionModalStore, usersStore, user)
  }

  const handleReactivateUser = async (userId: string) => {
    await usersStore.reactivateUser(userId)
    showAlert({
      title: t('users.activationEmailResent'),
      message: '',
      buttonText: t('btn.close'),
      isCloseShowing: false,
    })
  }

  const handleUnlockUser = async (userId: string) => {
    await usersStore.unlockUser(userId)
    showAlert({
      title: t('users.unlockedUser'),
      message: '',
      buttonText: t('btn.close'),
      isCloseShowing: false,
    })
  }

  const renderInvitedUsers = (visible: boolean) => {
    const { invitedUsers, isUserItemLoading } = usersStore
    return (
      <div className={classNames({ [style.tabHidden]: !visible })}>
        {invitedUsers.length > 0 ? (
          <UsersTab
            users={invitedUsers}
            handleEditUser={handleEditUser}
            handleReactivateUser={handleReactivateUser}
            isUserItemLoading={isUserItemLoading}
            handleResetPassword={handleResetPassword}
            type={'invited'}
          />
        ) : (
          <div className={style.noUsers}>{t('users.noInvitedUsers')}</div>
        )}
      </div>
    )
  }

  const renderActiveUsers = (visible: boolean) => {
    const { currentUser } = authStore
    const { activeUsers } = usersStore
    const canClone = canUserClone(currentUser)

    return (
      <div className={classNames({ [style.tabHidden]: !visible })}>
        {activeUsers.length > 0 ? (
          <UsersTab
            users={activeUsers}
            handleEditUser={handleEditUser}
            handleUserInfo={handleUserInfo}
            handleResetPassword={handleResetPassword}
            handleCloneUser={canClone && handleCloneUser}
            type={'active'}
          />
        ) : (
          <div className={style.noUsers}>{t('users.noActiveUsers')}</div>
        )}
      </div>
    )
  }

  const renderClonedUsers = (visible: boolean) => {
    const { clonedUsers } = usersStore

    return (
      clonedUsers.length > 0 && (
        <div className={classNames({ [style.tabHidden]: !visible })}>
          <UsersTab
            users={clonedUsers}
            handleResetPassword={handleResetPassword}
            handleUserInfo={handleUserInfo}
            handleEditUser={handleEditUser}
            type={'cloned'}
          />
        </div>
      )
    )
  }

  const renderLockedOutUsers = (visible: boolean) => {
    const { lockedUsers, isUserItemLoading } = usersStore
    return (
      lockedUsers.length > 0 && (
        <div className={classNames({ [style.tabHidden]: !visible })}>
          <UsersTab
            users={lockedUsers}
            handleEditUser={handleEditUser}
            handleUserInfo={handleUserInfo}
            handleUnlockUser={handleUnlockUser}
            isUserItemLoading={isUserItemLoading}
            handleResetPassword={handleResetPassword}
            type={'locked'}
          />
        </div>
      )
    )
  }

  const renderSuspendedUsers = (visible: boolean) => {
    const { suspendedUsers } = usersStore

    return (
      suspendedUsers.length > 0 && (
        <div className={classNames({ [style.tabHidden]: !visible })}>
          <UsersTab
            users={suspendedUsers}
            handleEditUser={handleEditUser}
            handleUserInfo={handleUserInfo}
            type={'suspended'}
          />
        </div>
      )
    )
  }

  const renderDeactivatedUsers = (visible: boolean) => {
    const { deactivatedUsers } = usersStore

    return (
      deactivatedUsers.length > 0 && (
        <div className={classNames({ [style.tabHidden]: !visible })}>
          <UsersTab
            users={deactivatedUsers}
            handleEditUser={handleEditUser}
            handleUserInfo={handleUserInfo}
            type={'deactivated'}
          />
        </div>
      )
    )
  }

  const renderExpiredPasswordUsers = (visible: boolean) => {
    const { expiredPasswordUsers } = usersStore

    return (
      expiredPasswordUsers.length > 0 && (
        <div className={classNames({ [style.tabHidden]: !visible })}>
          <UsersTab
            users={expiredPasswordUsers}
            handleEditUser={handleEditUser}
            handleResetPassword={handleResetPassword}
            handleUserInfo={handleUserInfo}
            type={'expired'}
          />
        </div>
      )
    )
  }

  const handleSearch = (value: string) => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - Search', {
      term: value,
      'Partner Id': currentPartnerId,
    })
    usersStore.searchInput.onChange(value)
    if (value && value.length > 2) {
      usersStore.handleSearch(value)
    } else {
      usersStore.handleEndSearch()
    }
  }

  const handleUserExport = () => {
    const currentPartnerId = authStore.currentUser?.company?.partnerId

    mixpanelActions.track('Users - Export Users', {
      'Partner Id': currentPartnerId,
    })

    if (!usersStore.usersExportProgress) {
      usersStore.initUserExport()
      const toastId = notify({
        title: t('users.exportNotifyHeading'),
        body: t('users.exportNotifyContent'),
        icon: <CsvPercentageRing />,
        mustDismiss: true,
        showDismiss: false,
      })
      usersStore.setUsersExportToastId(toastId)
    }
  }

  const { currentUser } = authStore
  if (!authStore.currentUser || !usersStore) {
    return <Loader />
  }

  if (currentUser.isEmployee && !window.localStorage.getItem('selectedEmployeeCompany')) {
    return <Redirect to="/select-partner" />
  }

  if (!authStore.currentUser.company) {
    return <Loader />
  }

  const { hasUserAccess } = appStore.accountSetupStore
  const hasAccess = hasUserAccess(window.location.pathname, authStore.currentUser)

  const {
    users,
    isLoading,
    isSearchActive,
    isUserItemLoading,
    usersError,
    setNotificationUserColumnsShown,
    notificationUserColumnsShown,
    currentTab,
    setCurrentTab,
  } = usersStore

  const isTrialExpiredOrTerminated = !currentUser?.isEmployee && isTerminated(currentUser?.company)
  // restricting add-user from standard users.
  // restricting add-user from admins with non NDA signed company.
  // const adminUserAndNotNDASigned =
  //   !authStore.currentUser.company.signedNdaTimestamp &&
  //   currentUserRole === PortalRole.admin
  // const addUserRestricted = standardUser || adminUserAndNotNDASigned
  const canClone = canUserClone(currentUser)

  const activeUserCount = usersStore.activeUsers.length
  const invitedUserCount = usersStore.invitedUsers.length
  const suspendedUserCount = usersStore.suspendedUsers.length
  const deactivatedUsersCount = usersStore.deactivatedUsers.length
  const lockedUserCount = usersStore.lockedUsers.length
  const expiredUserCount = usersStore.expiredPasswordUsers.length
  const clonedUserCount = usersStore.clonedUsers.length

  const tabOptions = [
    {
      key: 'activeUsers',
      label: `${t(`users.active`)} (${activeUserCount})`,
      content: renderActiveUsers(true),
    },
    {
      key: 'invitedUsers',
      label: `${t(`users.invited`)} (${invitedUserCount})`,
      content: renderInvitedUsers(true),
    },
  ]

  if (suspendedUserCount > 0) {
    tabOptions.push({
      key: 'suspendedUsers',
      label: `${t(`users.suspended`)} (${suspendedUserCount})`,
      content: renderSuspendedUsers(true),
    })
  }
  if (deactivatedUsersCount > 0) {
    tabOptions.push({
      key: 'deactivatedUsers',
      label: `${t(`users.deactivated`)} (${deactivatedUsersCount})`,
      content: renderDeactivatedUsers(true),
    })
  }
  if (lockedUserCount > 0) {
    tabOptions.push({
      key: 'lockedUsers',
      label: `${t(`users.locked`)} (${lockedUserCount})`,
      content: renderLockedOutUsers(true),
    })
  }
  if (expiredUserCount > 0) {
    tabOptions.push({
      key: 'expiredUsers',
      label: `${t(`users.expired`)} (${expiredUserCount})`,
      content: renderExpiredPasswordUsers(true),
    })
  }
  if (canClone && clonedUserCount > 0) {
    tabOptions.push({
      key: 'clonedUsers',
      label: `${t(`users.cloned`)} (${clonedUserCount})`,
      content: renderClonedUsers(true),
    })
  }

  if (currentTab >= tabOptions.length) {
    setCurrentTab(0)
  }

  const oc = style.override

  return hasAccess ? (
    <div>
      {isTrialExpiredOrTerminated && <TrialExpiredOrTerminated />}
      <DashboardLayout currentUser={authStore.currentUser}>
        {isLoading ? (
          <Loader theme="small" rootClassName={style.loaderRoot} />
        ) : (
          <div className={style.userRoot}>
            {usersError && !isLoading && <ErrorScreen />}
            <div className={style.sectionRoot}>
              <Title title={t('users.headerTitle')} errorMode={usersError} />
              <div className={style.addUserButton}>
                <Button styleVariant="superprimary" onClick={handleAddUser}>
                  {t('btn.addUser')}
                </Button>
              </div>
            </div>
            <div className={style.sectionRoot}>
              <SearchInputField
                rounded
                min={3}
                onChange={e => handleSearch(e.target.value)}
                placeholder={t('users.searchPlaceholder')}
                messages={[{ status: 'hint', message: t('users.searchHint') }]}
                defaultValue={usersStore.searchInput.value}
              />
            </div>

            {isSearchActive ? (
              <UserSearchResult
                handleEditUser={handleEditUser}
                isUserItemLoading={isUserItemLoading}
                handleReactivateUser={handleReactivateUser}
                handleResetPassword={handleResetPassword}
                handleUnlockUser={handleUnlockUser}
                handleCloneUser={handleCloneUser}
                handleUserInfo={handleUserInfo}
              />
            ) : (
              <div {...(usersError ? { className: style.userErrorMode } : {})}>
                <div className={style.userStatusFilterWrapper}>
                  <Toggler
                    type={'togglerTabs'}
                    value={tabOptions[currentTab]?.key}
                    toggleElements={tabOptions}
                    onToggle={te => setCurrentTab(tabOptions.findIndex(to => to.key === te.key))}
                  />

                  <Button
                    classes={curr => ({
                      ...curr,
                      container: classNames(curr.container, oc, style.userColumnSwitcher),
                      root: classNames(curr.root, oc, style.userColumnSwitcherButton),
                    })}
                    styleVariant="primary"
                    onClick={() => setNotificationUserColumnsShown(!notificationUserColumnsShown)}
                  >
                    <Icons.FilterIcon className={style.filterIconSize} />
                    {notificationUserColumnsShown
                      ? t('users.showUserDetails')
                      : t('users.showEmailAlerts')}
                  </Button>
                </div>
                <div>{tabOptions[currentTab]?.content}</div>

                {users.length < 2 ? (
                  <div>
                    <div className={style.lonelyText}>{t('users.lonelyText')}</div>
                    <div className={style.lonelyTextDesc}>{t('users.lonelyTextDesc')}</div>
                    <div className={style.lonelyBtnWrapper}>
                      <span onClick={handleAddUser}>{t('btn.inviteUser')}</span>
                    </div>
                  </div>
                ) : (
                  <Export onClick={handleUserExport} />
                )}
              </div>
            )}
            {!authStore.currentUser.company.signedNdaTimestamp && (
              <Box classes={curr => ({ root: classNames(curr.root, style.ndaBoxStyle) })}>
                {t('nda.message')}
              </Box>
            )}
          </div>
        )}
      </DashboardLayout>
    </div>
  ) : (
    <Redirect
      to={{
        pathname: '/missing-access',
        state: { from: props.location },
      }}
    />
  )
})

export const DashboardUsers = DashboardUsersComponent
